import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useLang } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { StatusBadge } from "../components/badges";
import { Skeleton } from "../components/ui/skeleton";

export default function Projects() {
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState(null);
  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data)).catch((e) => toast.error(apiError(e)));
  }, []);
  if (!projects) return <Skeleton className="h-60 w-full" />;
  return (
    <div className="space-y-6" data-testid="projects-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">{t("projects")}</h1>
      {projects.length === 0 && (
        <div className="apha-card p-8 text-sm text-muted-foreground" data-testid="projects-empty">
          {t("noProjectsHint")}
          {user.role === "ADMIN" && (
            <button data-testid="projects-empty-admin-link" onClick={() => navigate("/admin")} className="ml-2 text-[#1E3A8A] font-medium hover:underline">{t("goToAdmin")} →</button>
          )}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {projects.map((p) => (
          <div key={p.id} data-testid={`project-card-${p.key}`} role="button" tabIndex={0}
            onClick={() => navigate(`/projects/${p.id}`)}
            onKeyDown={(e) => e.key === "Enter" && navigate(`/projects/${p.id}`)}
            className="apha-card apha-hover p-5 text-left flex flex-col gap-3 cursor-pointer">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="text-xs text-muted-foreground font-medium">{p.key}</div>
                <div className="font-heading font-semibold">{p.name}</div>
              </div>
              {p.health && <StatusBadge status={p.health.status} />}
            </div>
            <div className="text-xs text-muted-foreground space-y-1">
              <div>{p.client} · PM: {p.pm}</div>
              {p.health && (
                <div className="flex items-center gap-3">
                  <span>{t("healthScore")}: <span className="font-medium text-foreground">{p.health.score}</span></span>
                  <span>{t("coverage")}: {p.health.coverage?.percentage ?? "—"}%</span>
                  <span>{t("warnings")}: {p.open_warnings}</span>
                </div>
              )}
              {p.next_milestone_date && <div>Milestone: {p.next_milestone_date}</div>}
            </div>
            <div className="flex items-center gap-3 text-xs font-medium text-[#1E3A8A] pt-1 border-t">
              <button data-testid={`project-board-link-${p.key}`} onClick={(e) => { e.stopPropagation(); navigate(`/projects/${p.id}/board`); }} className="hover:underline">{t("board")} →</button>
              {(p.methodology || "SCRUM") === "SCRUM" && <button data-testid={`project-backlog-link-${p.key}`} onClick={(e) => { e.stopPropagation(); navigate(`/projects/${p.id}/backlog`); }} className="hover:underline">{t("backlog")} →</button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
